import { reactive, readonly } from 'vue';
import type {
  Toast,
  ToastOptions,
  ToastServiceOptions,
  ToastPosition,
} from './types';
import { MessageStatusOptions } from '../../components/message/types';

class ToastService {
  private toasts = reactive<Toast[]>([]);
  private timers = new Map<string, NodeJS.Timeout>();
  private defaultDuration = 5000;
  private defaultPosition: ToastPosition = 'top-right';
  private maxToasts = 10;
  private idCounter = 0;

  constructor(options?: ToastServiceOptions) {
    if (options?.defaultDuration) {
      this.defaultDuration = options.defaultDuration;
    }
    if (options?.maxToasts) {
      this.maxToasts = options.maxToasts;
    }
  }

  private generateId(): string {
    return `toast-${++this.idCounter}-${Date.now()}`;
  }

  private addToast(options: ToastOptions): string {
    const id = options.id || this.generateId();
    const toast: Toast = {
      ...options,
      id,
      position: options.position || this.defaultPosition,
      createdAt: Date.now(),
    };

    // Remove oldest toast if we're at max capacity
    if (this.toasts.length >= this.maxToasts) {
      const oldestToast = this.toasts[0];
      this.remove(oldestToast.id);
    }

    // Add new toast
    this.toasts.push(toast);

    // Set auto-dismiss timer if duration is specified
    const duration = options.duration ?? this.defaultDuration;
    if (duration > 0) {
      const timer = setTimeout(() => {
        this.remove(id);
      }, duration);
      this.timers.set(id, timer);
    }

    return id;
  }

  show(options: ToastOptions): string {
    return this.addToast(options);
  }

  info(title: string, body?: string, options?: Partial<ToastOptions>): string {
    return this.show({
      ...options,
      status: MessageStatusOptions.INFO,
      size: options?.size || 'base',
      title,
      body,
    });
  }

  success(
    title: string,
    body?: string,
    options?: Partial<ToastOptions>
  ): string {
    return this.show({
      ...options,
      status: MessageStatusOptions.SUCCESS,
      size: options?.size || 'base',
      title,
      body,
    });
  }

  warning(
    title: string,
    body?: string,
    options?: Partial<ToastOptions>
  ): string {
    return this.show({
      ...options,
      status: MessageStatusOptions.WARNING,
      size: options?.size || 'base',
      title,
      body,
    });
  }

  error(title: string, body?: string, options?: Partial<ToastOptions>): string {
    return this.show({
      ...options,
      status: MessageStatusOptions.ERROR,
      size: options?.size || 'base',
      title,
      body,
    });
  }

  remove(id: string): void {
    const index = this.toasts.findIndex((toast) => toast.id === id);
    if (index > -1) {
      const toast = this.toasts[index];

      // Clear timer if exists
      const timer = this.timers.get(id);
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(id);
      }

      // Call onClose callback if provided
      if (toast.onClose) {
        toast.onClose();
      }

      // Remove toast
      this.toasts.splice(index, 1);
    }
  }

  removeAll(): void {
    // Clear all timers
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();

    // Call onClose callbacks
    this.toasts.forEach((toast) => {
      if (toast.onClose) {
        toast.onClose();
      }
    });

    // Clear all toasts
    this.toasts.splice(0, this.toasts.length);
  }

  getToasts(): readonly Toast[] {
    return readonly(this.toasts);
  }

  getToastsByPosition(position: ToastPosition): readonly Toast[] {
    return readonly(this.toasts.filter((toast) => toast.position === position));
  }
}

// Create singleton instance
const toastService = new ToastService();

// Export singleton instance and factory for custom instances
export { toastService, ToastService };
